import * as React from 'react';
import Link from 'next/link';
import { ChevronsDown } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';
import { cn } from '@/components/atoms/primitives/utils';
import { LociDotsMark, LociLettermarkMask } from '@/components/atoms/LociLettermark';
import {
  GLOBAL_NAV_ACCENT_VARS,
  GN_ACTIVE_LINK_GLOW_STATIC,
} from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import { WayfinderSheet } from '@/components/molecules/WayfinderSheet';
import { CityAccountChooser } from '@/components/molecules/CityHeader/CityAccountChooser';
import { CityAccountMenu } from '@/components/molecules/CityHeader/CityAccountMenu';
import { CityHeaderSearch } from '@/components/molecules/CityHeader/CityHeaderSearch';
import { CityNavMenu } from '@/components/molecules/CityHeader/CityNavMenu';
import { CategoriesDisclosure } from '@/components/molecules/CategoriesDropdown/CategoriesDisclosure';
import { appUrl, cityAppEntryUrl, defaultCityCode } from '@/lib/cross-host-urls';

// Re-export the shared nav/action primitives from the header entrypoint so
// existing import sites (`@/components/molecules/CityHeader`) keep working.
// `export … from` does not introduce a local binding, so the locally-used
// names are imported separately below.
export {
  CityAuthAvatar,
  PendingAccountAffordance,
  ACCOUNT_AFFORDANCE_ARIA_LABEL,
  ACCOUNT_CHOOSER_ARIA_LABEL,
  PENDING_ACCOUNT_ARIA_LABEL,
  ACCOUNT_AFFORDANCE_CONTROL,
  CHOOSER_SIGN_IN_LINK,
  CHOOSER_SIGN_UP_LINK,
  ActionLink,
  authedAccountAriaLabel,
  cityNavSignUpHref,
  FOCUS_RING,
  NAV_HUB_LINKS,
  NAV_ITEMS,
  OPEN_APP_LINK,
  SECONDARY_NAV_ITEMS,
  SIGN_IN_LINK,
  SIGN_UP_LINK,
  isCrossOrigin,
  type CityAuthAvatarProps,
  type PendingAccountAffordanceProps,
  type ActionLinkProps,
  type CityHeaderActiveNav,
  type CitySecondaryNavId,
  type CitySecondaryNavItem,
  type NavDisclosureItem,
  type NavItem,
  type NavLinkItem,
} from '@/components/molecules/CityHeader/cityHeaderShared';

import {
  PendingAccountAffordance,
  FOCUS_RING,
  NAV_ITEMS,
  resolveNavItemHref,
  type CityHeaderActiveNav,
} from '@/components/molecules/CityHeader/cityHeaderShared';
import { SurfaceLink } from '@/components/atoms/SurfaceLink';
import { useDiscoverHref } from '@/hooks/useDiscoverHref';
import type { CityAuthUser } from '@/lib/city-nav/cityAuthUser';
import type { CategoryTonightCounts } from '@/hooks/useCategoryTonightCounts';

export interface CityHeaderProps {
  /** City display name, used in the mono city label + Wayfinder accessible name. */
  cityName: string;
  /** Which primary nav item is the current page. */
  activeNav: CityHeaderActiveNav;
  /**
   * Path to the lockup SVG threaded through to the `CityNavMenu` chrome-row
   * lockup (FRE-1361: the top bar's own brand mark is now the fixed national
   * dots + lettermark composition and does not consume this prop). Default
   * points at the national lettermark; multi-city editions can override the
   * menu lockup per-city.
   */
  lockupSrc?: string;
  /** Destination for the "Open app" CTA. Defaults to the LOCI app entry point. */
  openAppUrl?: string;
  /**
   * Signup destination for the account chooser + sitemap (WEB-617 / DES-59).
   * Defaults to cross-host signup with `next=<this page>` (FRE-1389);
   * `?from=city-nav` is appended at render time.
   */
  signUpUrl?: string;
  /**
   * Sign-in destination for the account chooser + sitemap auth block
   * (WEB-614 / F-04 / FRE-1389). Defaults to cross-host login with
   * `next=<this page>` + `force=1`.
   */
  signInUrl?: string;
  /** City code for the Wayfinder layer switcher. Defaults to the registry default. */
  cityCode?: CityCode;
  /**
   * Signed-in identity for the DES-60 auth swap (WEB-640). When set, the
   * tools-cluster account control becomes the avatar that opens the account
   * drawer (FRE-1389) and the sitemap drops its anon sign-up block. Omit /
   * `null` keeps today's anon chrome byte-stable.
   */
  authUser?: CityAuthUser | null;
  /**
   * Cookie/hint-armed `/auth/me` still in flight with no SSR seed (WEB-640).
   * Renders a neutral account slot — never the anon CTA — until the probe
   * settles. Ignored when `authUser` is set.
   */
  isAuthPending?: boolean;
  /**
   * Request-host staging flag (WEB-776). Threaded into the account slot
   * (chooser, pending link, account drawer) so a missing
   * `NEXT_PUBLIC_LOCI_STAGE` cannot pin its hosts to prod.
   */
  isStaging?: boolean;
  /**
   * Tonight counts for the Categories disclosure + sitemap chips (WEB-1180).
   * Omit (default) and both fetch lazily on first intent; pass a value to seed
   * them (Storybook / a future SSR seed), or `null` to render without counts.
   */
  categoryTonightCounts?: CategoryTonightCounts | null;
  /** Additional CSS classes applied to the outer `<header>`. */
  className?: string;
}

/**
 * Always-on glass bar surface (FRE-1362 / DES-84, 2026-09-27 ruling: option
 * B). Dark matches the DS `.o-globalnav__bar` literal (`rgba(15,20,31,.78)` +
 * 12% border) via `.city-header` tokens; light remaps `--gn-bar-bg` /
 * `--gn-bar-border` in `styles/surfaces/city.css`. There is no scroll-solidify state — the
 * chrome is sticky in flow and the hero starts below it, so "transparent
 * over the hero" only ever showed the flat page ground, visually
 * indistinguishable from this same glass tint. Apex and app chrome are
 * already always-glass; one nav model per surface.
 */
const HEADER_SURFACE_SOLID = 'border-[color:var(--gn-bar-border)] bg-[var(--gn-bar-bg)]';

// WEB-333 repaint — DS 1.2.0 `o-globalnav__link`: Space Grotesk 13px / 500,
// `--r-md` radius, `--fg-2` → `--fg-1` + glass wash on hover. Padding,
// icon-gap, and letter-spacing live in the `.city-header-nav-link` component
// class (styles/components/nav-lockup.css) — see FRE-1362 for the 721–1024 tablet override.
// Do NOT add `whitespace-nowrap` here: nowrap alone stops wrap and starts
// overflow; the band must afford the content.
const NAV_LINK_BASE =
  'relative inline-flex shrink-0 min-h-[44px] desktop:min-h-0 items-center rounded-ds-md font-display text-small font-medium leading-tight text-fg-2 transition-colors duration-150 hover:bg-white/[0.06] hover:text-fg-1 motion-reduce:transition-none';

// Active = aria-current="page" + glowing accent underline (no filled pill).
const NAV_LINK_ACTIVE = `text-fg-1 ${GN_ACTIVE_LINK_GLOW_STATIC}`;

// FRE-1361 / DES-85: the top-bar brand mark itself is now a fixed national
// dots + lettermark composition (`LociDotsMark` + `LociLettermarkMask`,
// rendered directly below) — the city teal accent still drives the nav
// underline / glow / CTAs, NOT the wordmark. `DEFAULT_LOCKUP_SRC` remains
// only to thread the menu-sheet lockup (`CityNavMenu` → `NavMenuLockup`),
// which still branches on `src`.
const DEFAULT_LOCKUP_SRC = '/brand/loci-lettermark.svg';
// Canonical authed-app entry (e.g. https://app.loci.city/clt). Shared with
// the home hero + footer download strip via `cityAppEntryUrl()` so all three
// "Open app" CTAs resolve to the same destination (WEB-329).
// Call-time defaults so staging is resolved the same way as `crossHostLogin`
// / `cityAppEntryUrl` (WEB-773 / WEB-776). A module-level string literal
// pinned every surface — including staging — to production hosts.
const defaultOpenAppUrl = (): string => cityAppEntryUrl();
const defaultSignUpUrl = (isStaging?: boolean): string =>
  appUrl({ city: null, path: '/signup', isStaging });

/**
 * CityHeader — primary navigation chrome for the marketing-city surface,
 * aligned to the DS 1.7.0 global-nav bar (WEB-333 → FRE-873 → WEB-616 →
 * FRE-1361 / DES-85 → FRE-1362):
 *
 *  - 56px glass bar: always-on glass, no scroll state (FRE-1362 / DES-84,
 *    2026-09-27 ruling). `blur(18px) saturate(1.4)` at every scroll position.
 *  - FRE-1361 reverses WEB-616: the brand (dot-cluster symbol + lettermark +
 *    mono `· CHARLOTTE`) is its own `<a href="/">` home link again. The
 *    Wayfinder trigger is a separate 22px round switch beside it — the
 *    lockup no longer opens the sheet.
 *  - Primary links: Space Grotesk 13px, glowing accent underline on the
 *    `aria-current="page"` item, left-aligned right after the brand + switch
 *    (design `.o-globalnav__primary`, FRE-1362 reverses the old centered
 *    `flex-1 justify-center` band; supersedes FRE-1388's interim fix). A
 *    `flex-1` spacer sits between the nav and the tools cluster, so free
 *    space collects there instead. Visible from 721px up — below that the
 *    sitemap-menu takeover (`CityNavMenu`) is the sole hub path (WEB-615
 *    retired the bottom dock).
 *  - Tools cluster: search · account control · menu. Anon: the account icon
 *    opens the sign-in / sign-up chooser (`CityAccountChooser`, FRE-1389).
 *    Signed in: the avatar opens the account drawer (`CityAccountMenu`).
 *    The menu (`CityNavMenu` → the shared `NavMenuSheet`, WEB-613 → WEB-629 →
 *    FRE-1362) is the sitemap, a full-height takeover visible ≤720px only —
 *    a width rule, never an auth rule (DS 1.9.1).
 *
 * Server-default presentational component. Active state is driven by
 * `activeNav`. Interactive islands are extracted into `'use client'`
 * subcomponents so the header itself never opts the whole tree into the
 * client bundle — `WayfinderSheet` (composed lockup trigger), `CityHeaderSearch`
 * (SearchSheet trigger), `CityAccountChooser` / `CityAccountMenu` (account
 * slot), and `CityNavMenu` (the city host for the shared full-height
 * `NavMenuSheet` shell — it owns the trigger and open state).
 */
export function CityHeader({
  cityName,
  activeNav,
  lockupSrc = DEFAULT_LOCKUP_SRC,
  openAppUrl: _openAppUrl = defaultOpenAppUrl(),
  signUpUrl,
  // Intentionally no default — the menu falls back to
  // `crossHostLogin(currentUrl, { force: true })` (FRE-784 / F-04 / WEB-898).
  // A bare `/login` default would drop `force=1` and bounce stale-cookie
  // visitors; dropping `currentUrl` would drop the return destination.
  signInUrl,
  cityCode = defaultCityCode(),
  authUser = null,
  isAuthPending = false,
  isStaging,
  categoryTonightCounts,
  className,
}: CityHeaderProps): React.ReactElement {
  // Resolve after destructure so `isStaging` can reach `appUrl` when the
  // caller omits `signUpUrl` (WEB-776). A default-arg `defaultSignUpUrl()`
  // ran before `isStaging` was in scope and pinned SSR chrome to prod.
  const resolvedSignUpUrl = signUpUrl ?? defaultSignUpUrl(isStaging);
  // WEB-1231 / R25: Discover / This week keep a signed-in viewer on `app.*`.
  const toDiscover = useDiscoverHref(cityCode);
  return (
    <header
      style={GLOBAL_NAV_ACCENT_VARS.city}
      className={cn(
        'city-header page-chrome-inline flex h-14 w-full items-center border-b backdrop-blur-[18px] backdrop-saturate-[1.4]',
        HEADER_SURFACE_SOLID,
        className,
      )}
      data-testid="city-header"
    >
      <div className="flex min-w-0 items-center" data-testid="city-header-lockup">
        {/*
          FRE-1361 / DES-85 (reverses WEB-616 / PL-08): the brand is its own
          home link again — dot-cluster symbol (constant brand amber) +
          lettermark (themes via `--nav-lettermark`: ink on light, cream on
          dark) + the mono `· {city}` label. The Wayfinder trigger is now a
          SEPARATE 22px round switch beside it (below) — it no longer wraps
          the lockup, so `group-hover`/open-state styling on the label keys
          off the brand link's own hover, not the switch's popover state.

          WEB-722: below `md` the nowrap `· {city}` plus three 44px tools
          (search / account / menu) measured 374px at a 320 viewport and
          expanded `documentElement.scrollWidth`. Hide the redundant label
          on that band — the coord-strip eyebrow already names the host.
        */}
        <Link
          href="/"
          aria-label={`LOCI ${cityName}, home`}
          data-testid="city-header-brand"
          className={cn(
            'group inline-flex min-w-0 shrink-0 items-center gap-[7px] rounded-ds-md px-1.5 py-1',
            'transition-colors duration-150 hover:bg-white/[0.06] motion-reduce:transition-none',
            FOCUS_RING,
          )}
        >
          <LociDotsMark />
          <LociLettermarkMask />
          <span
            aria-hidden="true"
            data-testid="city-header-city-label"
            className={cn(
              // FRE-1361: matches the design's own `loci-globalnav-page.css`
              // ≤440 rule exactly (was the wider `md:` / 768px cut carried
              // over from the old composed-trigger layout, WEB-722) — the
              // new brand + separate 22px switch reclaim enough width that
              // the label can come back above 440. `.city-header-city-label`
              // (styles/components/nav-lockup.css, `@layer components`) owns the display
              // toggle: `none` ≤440, `inline-flex` from 441px.
              // FRE-1388: no left rule. The design's `border-left` divider
              // (+ its 10px text offset) is deleted at every breakpoint by
              // operator ruling — the `·` is the only separator, spaced off
              // the wordmark by the brand's 7px gap.
              'city-header-city-label items-center whitespace-nowrap font-mono text-micro uppercase tracking-[0.14em]',
              'text-[color:var(--gn-accent-glow)]',
            )}
          >
            · {cityName}
          </span>
        </Link>
        <WayfinderSheet
          layer="city"
          cityCode={cityCode}
          cityName={cityName}
          // WEB-1052 (operator ruling 2026-09-29): the trigger is the 22px
          // painted switch (DS `.o-globalnav__switch`) on every pointer, so a
          // fine pointer hits the paint; on a coarse pointer `.a-hit-floor`
          // grows the hit box to 44 with an invisible `::before`. `min-h-0`
          // drops the WayfinderSheet default `min-h-11`.
          className="a-hit-floor ml-0.5 size-5.5 min-h-0 shrink-0 justify-center rounded-full"
        >
          <span
            data-testid="city-header-wayfinder-switch"
            className={cn(
              // `.city-header-wayfinder-switch` (styles/components/nav-lockup.css) sizes the
              // 22px circle; the `GlassControl` recipe paints it, and
              // `--face` lights it when the trigger (`group`) is hovered.
              'a-glass-control a-glass-control--face city-header-wayfinder-switch inline-flex items-center justify-center rounded-full',
              'text-fg-2 transition duration-150 motion-reduce:transition-none',
              'group-hover:text-[color:var(--gn-accent-glow)]',
            )}
          >
            <ChevronsDown aria-hidden="true" className="size-[11px]" strokeWidth={2} />
          </span>
        </WayfinderSheet>
      </div>

      {/*
        FRE-1362 (supersedes FRE-1388's interim fix): left-aligned directly
        after the Wayfinder switch, as in the design. `.city-header-primary-nav`
        (styles/components/nav-lockup.css) owns display / gap / margin: its 16px
        margin puts the first link box 16px from the painted circle — the
        design's bar gap 10px + `.o-globalnav__primary` margin-left 6px. The
        Wayfinder trigger is its 22px paint (WEB-1052), so there is no hit-box
        overhang to add or cancel.
      */}
      <nav
        aria-label="Primary"
        className="city-header-primary-nav"
        data-testid="city-header-primary-nav"
      >
        {NAV_ITEMS.map((item) => {
          // WEB-1180 / DES-108: Categories is a disclosure, never a route —
          // its caret opens the ten `/category/<slug>` hubs. `activeNav`
          // still drives the trigger's `aria-current` (`/category/*`).
          if (item.kind === 'disclosure') {
            return (
              <CategoriesDisclosure
                key={item.id}
                cityName={cityName}
                cityCode={cityCode}
                isCurrent={item.id === activeNav}
                counts={categoryTonightCounts}
                className={cn(NAV_LINK_BASE, 'city-header-nav-link', FOCUS_RING)}
                activeClassName={NAV_LINK_ACTIVE}
              />
            );
          }
          const isActive = item.id === activeNav;
          return (
            <SurfaceLink
              key={item.id}
              href={resolveNavItemHref(item, toDiscover)}
              aria-current={isActive ? 'page' : undefined}
              data-active={isActive ? 'true' : undefined}
              className={cn(
                NAV_LINK_BASE,
                'city-header-nav-link',
                FOCUS_RING,
                isActive && NAV_LINK_ACTIVE,
              )}
              data-testid={`city-header-nav-${item.id}`}
            >
              {item.label}
            </SurfaceLink>
          );
        })}
      </nav>

      {/* Design `.o-globalnav__spacer` (`flex:1`) — free space collects here,
          between the primary nav and the tools cluster, instead of around a
          centered nav (FRE-1362). */}
      <div className="flex-1" aria-hidden="true" />

      <div className="flex shrink-0 items-center gap-1.5" data-testid="city-header-actions">
        <CityHeaderSearch cityName={cityName} />
        {/* WEB-617 / DES-59 / WEB-640 / FRE-1389: single account slot —
            anon icon → chooser, pending neutral link, or avatar → account
            drawer. Never more than one; control count must stay stable
            (WEB-634 discipline). The menu follows it for EVERY visitor:
            it is a width rule, not an auth rule (DS 1.9.1). */}
        {authUser ? (
          <CityAccountMenu
            cityCode={cityCode}
            cityName={cityName}
            authUser={authUser}
            lockupSrc={lockupSrc}
            isStaging={isStaging}
          />
        ) : isAuthPending ? (
          <PendingAccountAffordance signUpUrl={resolvedSignUpUrl} />
        ) : (
          <CityAccountChooser
            cityCode={cityCode}
            signUpUrl={signUpUrl}
            signInUrl={signInUrl}
            isStaging={isStaging}
          />
        )}
        <CityNavMenu
          cityName={cityName}
          cityCode={cityCode}
          lockupSrc={lockupSrc}
          activeNav={activeNav}
          signUpUrl={signUpUrl}
          signInUrl={signInUrl}
          authUser={authUser}
          isAuthPending={isAuthPending}
          categoryTonightCounts={categoryTonightCounts}
        />
      </div>
    </header>
  );
}
